import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import RenderIf from "common/components/RenderIf";

import { useActiveConversationContext } from "../../context/ActiveConversationContext";
import useConversationMessages from "../../datahooks/useConversationMessages";
import useConversationUpdates from "../../datahooks/useConversationUpdates";
import useActiveConversationState from "../../hooks/useActiveConversationState";
import { useNewConversation } from "../../hooks/useNewConversation";
import useSNConversation from "../../hooks/useSNConversation";
import InboxHeader from "./InboxHeader";
import MessageContainer from "./MessageField/MessageContainer";
import MessageThread from "./MessageThread";
import NotificationArea from "./NotificationArea";

import SendMessageImage from "assets/images/empty-placeholders/send-a-message.png";

interface ConversationContainerProps {
  showLeadPreview: (leadId: string) => void;
}

function ConversationContainer({
  showLeadPreview,
}: ConversationContainerProps) {
  const shouldScrollToMessageRef = useRef<boolean>(false);

  const { activeConversationState } = useActiveConversationState();
  const { activeConversation } = useActiveConversationContext();
  const { newConversation } = useNewConversation();
  const { isSNConversation, hasSNContract } =
    useSNConversation(activeConversation);

  const { messages, isLoadingMessages, loadMessages, hasMoreMessages } =
    useConversationMessages(
      activeConversation && {
        unreadCount: activeConversation.unread_count,
        accountId: activeConversationState.accountId,
        conversationUrn: activeConversation.conversation_urn,
        lastActivityAt: activeConversation.last_activity_at,
      },
    );
  const { markAsRead } = useConversationUpdates();

  const [editMessage, setEditMessage] = useState(null);

  function scrollToBottom() {
    shouldScrollToMessageRef.current = true;
  }

  useEffect(() => {
    if (activeConversation?.unread_count) {
      markAsRead({
        accountId: activeConversation.owner,
        conversationUrn: activeConversation.conversation_urn,
      });
    }
    setEditMessage(null);
  }, [activeConversationState]);

  if (!activeConversation && !newConversation) {
    return (
      <EmptyPlaceholder
        imageSrc={SendMessageImage}
        title="Select a conversation"
        subtitle="Chat with your leads directly through Aimfox"
      />
    );
  }

  let hasMessageFromAnotherUser = true;

  // Determine if the conversation has any messages from the other participant
  if (messages) {
    const accountId = activeConversation
      ? activeConversation.owner
      : newConversation.owner;
    hasMessageFromAnotherUser = messages.pages
      .flat()
      .some(({ sender }) => sender.id !== accountId);
  }

  const isMessageFieldVisible = !isSNConversation || hasSNContract;

  return (
    <>
      <InboxHeader showLeadPreview={showLeadPreview} />

      <MessageThread
        setEditMessage={setEditMessage}
        messages={messages}
        loadMoreMessages={loadMessages}
        hasMoreMessages={hasMoreMessages}
        isLoadingMessages={isLoadingMessages}
        showLeadPreview={showLeadPreview}
        shouldScrollToMessageRef={shouldScrollToMessageRef}
      />

      <div
        className={clsx(
          "flex flex-col gap-y-4",
          isMessageFieldVisible
            ? "lg:px-2 lg:pb-2"
            : "pb-4 lg:pb-8 lg:pt-6 px-4",
        )}
      >
        <NotificationArea
          hasMessageFromAnotherUser={hasMessageFromAnotherUser}
        />

        <RenderIf condition={isMessageFieldVisible}>
          <MessageContainer
            editMessage={editMessage}
            hasMessageFromAnotherUser={hasMessageFromAnotherUser}
            cancelEdit={() => {
              setEditMessage(null);
            }}
            scrollToBottom={scrollToBottom}
          />
        </RenderIf>
      </div>
    </>
  );
}

export default ConversationContainer;
